import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';

import type { RunExecutionStatus, ItemExecutionStatus } from '../../types/mqtt';

type Props = {
  runStatus: RunExecutionStatus | null;
  itemStatus: ItemExecutionStatus | null;
};

const getStepIcon = (
  stepIndex: number,
  currentStep: number,
  itemStatusStr: string | undefined
) => {
  // If the item is completed, all steps are done
  if (itemStatusStr === 'COMPLETED')
    return <CheckCircleIcon fontSize="small" color="success" />;
  if (itemStatusStr === 'FAILED' && stepIndex === currentStep)
    return <ErrorIcon fontSize="small" color="error" />;
  if (stepIndex < currentStep)
    return <CheckCircleIcon fontSize="small" color="success" />;
  if (stepIndex === currentStep) {
    if (itemStatusStr === 'SKIPPED')
      return <SkipNextIcon fontSize="small" color="disabled" />;
    return <PlayCircleIcon fontSize="small" color="primary" />;
  }
  return <RadioButtonUncheckedIcon fontSize="small" color="disabled" />;
};

const RUN_STATUS_COLORS: Record<
  string,
  'default' | 'success' | 'error' | 'warning' | 'info'
> = {
  PENDING: 'default',
  RUNNING: 'info',
  PAUSED: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  ABORTED: 'warning',
  REJECTED: 'error'
};

const ProgressTab: React.FC<Props> = ({ runStatus, itemStatus }) => {
  const totalItems = runStatus?.totalItems ?? 0;
  const currentItemIndex = runStatus?.currentItemIndex ?? 0;
  const isRunCompleted = runStatus?.status === 'COMPLETED';
  const isRunFailed =
    runStatus?.status === 'FAILED' || runStatus?.status === 'ABORTED';
  const runProgress =
    totalItems > 0
      ? isRunCompleted
        ? 100
        : (currentItemIndex / totalItems) * 100
      : isRunCompleted
        ? 100
        : 0;

  const totalSteps = itemStatus?.totalSteps ?? 0;
  const currentStep = itemStatus?.currentStep ?? 0;
  const isItemCompleted = itemStatus?.status === 'COMPLETED';
  const stepProgress =
    totalSteps > 0
      ? isItemCompleted
        ? 100
        : (currentStep / totalSteps) * 100
      : 0;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {runStatus && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={runStatus.status}
            size="small"
            color={RUN_STATUS_COLORS[runStatus.status] ?? 'default'}
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Box>
      )}

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Run Progress
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isRunCompleted ? totalItems : currentItemIndex}/{totalItems} items
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={runProgress}
          color={isRunFailed ? 'error' : isRunCompleted ? 'success' : 'primary'}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {itemStatus && (
        <Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              {isItemCompleted ? 'Last Item' : 'Current Item'}:{' '}
              {itemStatus.itemGlobalId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isItemCompleted ? totalSteps : currentStep}/{totalSteps} steps
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={stepProgress}
            color={isItemCompleted ? 'success' : 'primary'}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Box>
      )}

      {itemStatus && totalSteps > 0 && (
        <List dense sx={{ pt: 0 }}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <ListItem key={i} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {getStepIcon(i, currentStep, itemStatus.status)}
              </ListItemIcon>
              <ListItemText
                primary={`Step ${i + 1}`}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: isItemCompleted
                    ? 'text.secondary'
                    : i === currentStep
                      ? 'text.primary'
                      : 'text.secondary'
                }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {!runStatus && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No execution in progress
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProgressTab;
